import { formatLoad, formatRir } from "./loadDisplay";

// Phase 9D: the words for a rendered Suggested (§13.6, §13.10). Presentation
// only: it reads the 9C render result and never recomputes a value, a
// direction or a rep target. No confidence, grade or rule detail is shown.

const ARROWS = Object.freeze({ UP: "↑", SAME: "→", DOWN: "↓" });
const DIRECTION_WORDS = Object.freeze({
  UP: "heavier than last time",
  SAME: "same weight as last time",
  DOWN: "lighter than last time",
});

function validTargetRir(targetRir) {
  return Number.isInteger(targetRir) && targetRir >= 0 && targetRir <= 5;
}

// The visible rep guidance and its spoken form. They differ only where the
// visible text is an abbreviation ("RIR 2").
function repDetail(rendered, targetRir) {
  const visible = visibleRepDetail(rendered, targetRir);
  if (!visible) return { visible: "", spoken: "" };
  if (rendered.intent === "HOLD" && rendered.holdReason === "OVERSHOT" && validTargetRir(targetRir)) {
    const count = targetRir === 5 ? "5 or more reps" : targetRir === 1 ? "1 rep" : `${targetRir} reps`;
    return { visible, spoken: `aim for ${count} in reserve` };
  }
  return { visible, spoken: visible };
}

// A numeric rep target always names its unit ("aim for 10 reps").
function repCount(reps) {
  return `${reps} ${reps === 1 ? "rep" : "reps"}`;
}

function visibleRepDetail(rendered, targetRir) {
  const reps = rendered.repTarget;
  switch (rendered.intent) {
    case "STEP_UP":
    case "SAME_LOAD":
      return reps == null ? "" : `aim for ${repCount(reps)}`;
    case "BUILD_REPS":
      return reps == null ? "build reps" : `build to ${repCount(reps)}`;
    case "HOLD":
      // An overshoot holds the load and names today's prescribed RIR. Without
      // a usable target it falls back to the plain hold wording.
      if (rendered.holdReason === "OVERSHOT" && validTargetRir(targetRir)) {
        return `aim for ${formatRir(targetRir)}`;
      }
      return reps == null ? "hold" : `hold at ${repCount(reps)}`;
    default:
      return "";
  }
}

/**
 * Returns null, or { arrow, value, detail, label, fills }:
 *   value  - the main text: an exact load, a floor ("100 lb or more"), or a
 *            direction in words ("Go heavier")
 *   detail - the rep guidance, in words
 *   label  - the full spoken sentence; it never relies on the arrow
 *   fills  - true only for an exact numeric Suggested
 * `targetRir` is today's prescribed RIR for the set the Suggested sits on.
 */
export function suggestedCopy(rendered, { targetRir = null } = {}) {
  if (!rendered?.kind) return null;
  const arrow = ARROWS[rendered.direction] || "";
  const { visible: detail, spoken } = repDetail(rendered, targetRir);
  const detailSentence = spoken ? `, ${spoken}` : "";

  if (rendered.kind === "NUMERIC") {
    const value = formatLoad(rendered.load);
    return {
      arrow,
      value,
      detail,
      label: `Suggested: ${value}, ${DIRECTION_WORDS[rendered.direction]}${detailSentence}. Use this weight.`,
      fills: true,
    };
  }
  if (rendered.kind === "FLOOR") {
    // A starting floor, never an exact target.
    const floor = formatLoad(rendered.floor);
    return {
      arrow,
      value: `${floor} or more`,
      detail: "",
      label: `Suggested: start from at least ${floor}; heavier may suit today.`,
      fills: false,
    };
  }
  const heavier = rendered.direction === "UP";
  return {
    arrow,
    value: heavier ? "Go heavier" : "Go lighter",
    detail,
    label: `Suggested: go ${heavier ? "heavier" : "lighter"} than last time${detailSentence}.`,
    fills: false,
  };
}
